import {
  Avatar,
  CheckIconProps,
  Combobox,
  ComboboxProps,
  Flex,
  Group,
  Input,
  InputBase,
  ScrollArea,
  useCombobox,
} from "@mantine/core";
import { IUserPartial } from "@schemas/issueSchema";
import { IconCaretDownFilled, IconCheck } from "@tabler/icons-react";
import { useRef, useState } from "react";

function findItemByValue(value: string, data: IUserPartial[]): IUserPartial | null {
  return data.find((entry) => entry._id === value) ?? null;
}

const iconProps: CheckIconProps = {
  color: "currentColor",
  opacity: 0.6,
  size: 18,
};

type McMantineMultiUserSelectProps = Omit<ComboboxProps, "children" | "defaultValue" | "data" | "onChange"> & {
  data: IUserPartial[];
  /** Called ONLY when dropdown closes with final selection (not on Escape) */
  onChange?: (value: IUserPartial[]) => void;
  defaultValue?: IUserPartial[];
  placeholder?: string;
};

export function McMantineMultiUserSelect({
  data,
  onChange,
  defaultValue = [],
  placeholder,
  ...props
}: McMantineMultiUserSelectProps) {
  const [values, setValues] = useState<IUserPartial[]>(defaultValue);
  const [search, setSearch] = useState<string>("");
  const [hasOpenedOnce, setHasOpenedOnce] = useState<boolean>(false);
  const searchRef = useRef<HTMLInputElement>(null);

  const MAX_VISIBLE_AVATARS = 10;
  const hiddenCount = Math.max(0, values.length - MAX_VISIBLE_AVATARS);
  const visibleUsers = values.slice(0, MAX_VISIBLE_AVATARS);
  const usersToRender = hiddenCount > 0 ? values.slice(0, MAX_VISIBLE_AVATARS - 1) : visibleUsers;

  const combobox = useCombobox({
    onDropdownOpen: () => {
      setHasOpenedOnce(true);
      setSearch("");

      combobox.updateSelectedOptionIndex("active");
      setTimeout(() => searchRef.current?.focus(), 0);
    },
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      onChange?.(values);
    },
  });

  const toggleValue = (val: IUserPartial) =>
    setValues((current) =>
      current.some((v) => v._id === val._id) ? current.filter((v) => v._id !== val._id) : [...current, val],
    );

  const removeValue = (id: string) => setValues((current) => current.filter((v) => v._id !== id));

  const matchesSearch = (item: IUserPartial) => item.name.toLowerCase().includes(search.trim().toLowerCase());

  const renderOptionFromItem = (item: IUserPartial) => {
    const checked = values.some((v) => v._id === item._id);

    return (
      <Combobox.Option value={item._id} key={item._id} active={checked}>
        <Group flex="1" gap="xs" wrap="nowrap">
          <Avatar src={`/api/image/profilePictures/${item._id}`} name={item.name} size="sm" />
          <Flex style={{ minWidth: 0 }}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</span>
          </Flex>
          {checked && <IconCheck style={{ marginInlineStart: "auto" }} {...iconProps} />}
        </Group>
      </Combobox.Option>
    );
  };
  const avatarGroup = (
    <Avatar.Group
      spacing="xs"
      style={{
        flexWrap: "nowrap",
        overflow: "hidden",
        flexShrink: 0,
        minWidth: 0,
      }}
    >
      {usersToRender.map((item) => (
        <Avatar
          key={item._id}
          src={`/api/image/profilePictures/${item._id}`}
          name={item.name}
          size="sm"
          style={{ cursor: "pointer" }}
        />
      ))}

      {hiddenCount > 0 && (
        <Avatar size="sm" style={{ cursor: "pointer" }}>
          +{hiddenCount}
        </Avatar>
      )}
    </Avatar.Group>
  );

  const options = data.map((entry) => (matchesSearch(entry) ? renderOptionFromItem(entry) : null)).filter(Boolean);

  return (
    <Combobox
      store={combobox}
      resetSelectionOnOptionHover
      onOptionSubmit={(val) => {
        const entry = findItemByValue(val, data);
        if (!entry) return;
        toggleValue(entry);
      }}
      {...props}
    >
      <Combobox.Target>
        <InputBase
          styles={{
            input: {
              outline: "none",
              boxShadow: "none",
            },
          }}
          component="button"
          type="button"
          pointer
          rightSection={<IconCaretDownFilled size={17} color="var(--mc-icon-default)" />}
          rightSectionPointerEvents="none"
          onClick={() => combobox.toggleDropdown()}
        >
          {values.length > 0 ? (
            <Flex miw={0} style={{ overflow: "hidden" }}>
              {avatarGroup}
            </Flex>
          ) : (
            <Input.Placeholder>{placeholder ?? "Velg bruker"}</Input.Placeholder>
          )}
        </InputBase>
      </Combobox.Target>

      <Combobox.Dropdown>
        {data.length > 10 && (
          <Combobox.Search
            ref={searchRef}
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            placeholder="Søk..."
          />
        )}
        <Combobox.Options>
          {hasOpenedOnce && (
            <ScrollArea.Autosize mah={200} type="scroll">
              {options.length === 0 ? <Combobox.Empty>Ingen brukere funnet</Combobox.Empty> : options}
            </ScrollArea.Autosize>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
